'use strict';

import { toNorm } from './norm.js';

// ─── Unigram corpus & phrase segmenter ───────────────────────────────────────

export const UNIGRAM_CORPUS_URL = 'https://raw.githubusercontent.com/rspeer/wordfreq/master/wordfreq/data/large_en.msgpack.gz';
export const UNIGRAM_CORPUS_IDB_KEY = 'corpus_unigrams_decoded';
export const UNIGRAM_CORPUS_SIZE_KEY = 'corpus_unigrams_size';

export const SPACE_OUT_WINDOWS = { few: 5, many: 10 };
export const SPACE_OUT_PART_PENALTY = 7;
export const SPACE_OUT_BIGRAM_WEIGHT = 3;
export const SPACE_OUT_OOV_PER_LETTER = 1.5 * Math.LN10;
export const SPACE_OUT_MORPHEME_PENALTY = 1.0;
export const SPACE_OUT_SUFFIXES = ['s', 'es', 'ed', 'ied', 'ing', 'er', 'est', 'ly', 'ies'];
// Stem credit only, kept off SPACE_OUT_SUFFIXES because the compound reading peels and
// gates on that list as inflections. Each earns its place by almost never being a word
// of its own: the same credit for -ize glues CLASS SIZE and WIN A PRIZE into one word,
// and -less glues FAR LESS.
export const SPACE_OUT_DERIVED_SUFFIXES = [
  'ness', 'nesses', 'iness', 'inesses', 'ier', 'iest', 'ily', 'ful', 'fuls',
];
const Y_TO_I_SUFFIXES = new Set(['ies', 'ied', 'iness', 'inesses', 'ier', 'iest', 'ily']);
const SPACE_OUT_STEM_SUFFIXES = [...SPACE_OUT_SUFFIXES, ...SPACE_OUT_DERIVED_SUFFIXES];

// Manual space-out overrides: a glued part's norm → its forced spacing, applied
// per segmentation part so `ofthe → of the` fires mid-entry (ageofthepyramids),
// not just as a whole entry. Each row is written as the spacing alone, its glued key
// derived from it via toNorm, so the two can never drift apart.
//
// Rows carry real orthography (`I don't`, `on one's`, `New York`), not bare norms: the
// table ships to every user, so a rendering must not depend on whether that user's
// wordlist happens to store `I` capitalised. Dedup folds case and punctuation so a
// rich row still merges with the same spacing found by the scorer — see rankedSplits.
//
// Derived, not hand-picked: every multi-word entry of a real 750k-entry wordlist was
// segmented, each result diffed against that entry's own spacing, and the parts that
// swallow a real word boundary collected — ordered here by how many entries each one
// mis-glues. A fragment earns a row only if it mis-glues in 25+ entries and is either
// never a legitimate word in that corpus, or wrong-glued at least 10:1 against its
// real uses (capped at 5) and below a unigram-frequency floor. Those gates are what
// reject `up on` (100 glued against 258 real `upon`s) and `a long`, both of which read
// as plausibly as any row here — which is why rows belong to a re-run of the analysis
// rather than to eyeballing. Method and measured effect: docs/segmenter.md § Fixed rules.
const SPACE_OUT_SPACINGS = [
  'of the', 'in the', 'on the', 'out of', 'to the', 'like a', 'in a', 'for the', 'on a',
  'up to', 'and the', 'at the', 'as a', 'the world', 'to a', 'of a', 'up the', 'off the',
  'are you', 'for a', 'New York', 'do you', 'to be', 'take a', 'in on', "I don't", 'from the',
  'make a', 'made a', 'a good', 'the way', 'this is', 'took a', 'have a', 'all the', "I can't",
  'is that', "it's a", 'I am', 'back to', 'up a', 'by the', 'I have', 'I can', 'under the',
  'the last', 'it in', 'you know', 'it to', 'of it', "on one's", 'it all', 'can I', 'is a',
  'at a', 'I know', 'going to', 'the best', 'the day', 'into the', 'out the', 'am I', 'to me',
  'had a', 'ice cream', 'let me', 'of time', 'the time', 'down to', 'if you', 'got a',
  "in one's", 'not a', 'for you', 'on your', 'the road', 'what a', 'it a', 'over the',
  'the same', 'a lot', 'do it', 'of love', 'a bad', 'can you', 'if I', 'of life', 'what you',
  'get it', "of one's", 'to you', 'what I', 'with a', 'get a', 'I was', 'want to', 'has a',
  'I do', 'United States', 'the end', 'the house', 'the man', 'the sun', 'with you', 'a little',
  'end of', 'it on', 'just a', 'to know', 'New Jersey', 'I want', 'you want', 'it is', 'to it',
  'in love', 'in your', 'is the', 'look at', 'National Park', 'of God', 'to say', 'up and',
  'with me', 'a day', 'have to', 'I see', 'on it', 'do I', 'I need', 'make it', 'see you',
  "to one's", "up one's", 'of all', 'the year', 'you can', 'take it', 'the game', 'the right',
  'a few', 'a joke', 'be a', 'of my', 'you have', 'and a', 'I got', 'into a', 'the truth',
  'to see', 'all over', 'did I', 'Los Angeles', 'more than', 'with it', 'a bit', 'I could',
  'I love', "I'm a", 'in for', 'in it', 'in my', 'like it', 'up in', 'best of', 'against the',
  'by a', 'get out', 'got it', 'it was', 'you are', 'I get', 'of you', 'out with', 'the job',
  'high school', 'I think', 'the other', 'the red', 'a second', 'as I', 'at home', 'I just',
  'to an', 'about it', 'back in', 'do not', 'for me', 'I feel', 'need a', 'you think',
  'a right', 'around the', 'do the', 'of war', 'Star Trek', 'the wall', 'all in', 'a look',
  'as you', 'a time', 'away from', 'I said', 'the king', 'the whole', 'I say', 'me a',
  'state of', 'tell me', 'a la', 'a ride', 'from a', 'must be', 'my life', 'on top', 'that was',
  'to get', 'what is', 'a big', 'a cab', 'a job', 'at it', 'got to', 'is not', 'like the',
  'the top', 'your life', 'a thing', 'be the', 'for all', "I'm so", 'part of', 'put on',
  'thank you', 'the first', 'the new', 'a mile', 'and I', 'as the', 'I mean', 'in touch',
  'I will', 'my heart', 'no one', 'not to', 'we have', 'de la', 'keep it', 'North Carolina',
  'Star Wars', 'the bed', 'too much', 'we are', 'what the', 'you see',
];

// A Map, not an object literal: parts are arbitrary wordlist norms, and `constructor`
// is both a real entry and an inherited Object key, so a plain object resolves it to
// a function and throws mid-split.
export const SPACE_OUT_OVERRIDES = new Map(
  SPACE_OUT_SPACINGS.map(spacing => [toNorm(spacing), spacing]));

// Bound affixes that reach the scorer as parts, because some wordlist carries them as
// entries or they are short enough to need none, and that it strands beside the word
// they belong to: ABATER S, UN ABATING, SULFUR IZE. joinAffixes puts them back.
//
// Mined like the overrides: a fragment qualifies when a 738k-entry merge strands it 100+
// times and the 54k really-spaced entries of a real wordlist break on at most two. That
// gate is what keeps out the free words that look like affixes. DE breaks 32 phrases
// (CLAIR DE LUNE), FORE swallows the AND of FORE AND AFT, a lone Y, E, N, L, or D ends
// LETTER Y and GUNS N ROSES, and ABLE, ALLY, ION, and IAN are words.
export const SPACE_OUT_BOUND_SUFFIXES = new Set([
  's', 'ism', 'isms', 'ist', 'ists', 'ic', 'ish', 'ier', 'iest', 'ily', 'ity', 'ful', 'ably',
  'ment', 'ments', 'ation', 'ations',
  'ize', 'izes', 'ized', 'ization', 'izations', 'ise', 'ises', 'ised', 'ising', 'isation', 'isations',
  // Reached only by the linking-i merge below, so each begins with the i it swallows.
  'ia', 'izing', 'ities', 'iform', 'iforms', 'iformes', 'ification', 'ifications', 'ified', 'iflora',
]);
export const SPACE_OUT_BOUND_PREFIXES = new Set([
  're', 'un', 'non', 'pre', 'mis', 'dis', 'co', 'anti', 'semi', 'inter', 'hyper', 'micro',
]);

// A suffix needs a word of 2+ letters before it, so H M S PINAFORE keeps its S. A prefix
// needs 3+ letters after it, so it cannot swallow a TO or an OF. Joins chain, which is
// how CHANNEL IS ATION comes back through ISATION.
function joinAffixes(parts) {
  const out = [];
  let prefix = '';
  parts.forEach((part, i) => {
    const next = parts[i + 1];
    if (SPACE_OUT_BOUND_PREFIXES.has(part) && next !== undefined && toNorm(next).length >= 3) {
      prefix += part;
      return;
    }
    // A derivational ending starting with i (ACRONYM IZING) reaches the scorer split,
    // because a lone i is a cheap real word. Merged forward only when the pair spells a
    // listed ending, so the pronoun in ALL I DO is untouched. Compared raw, so an
    // override's capital `I` — always the pronoun — never merges.
    if (part === 'i' && next !== undefined && SPACE_OUT_BOUND_SUFFIXES.has('i' + next)) {
      prefix += part;
      return;
    }
    out.push(prefix + part);
    prefix = '';
    while (out.length >= 2 && SPACE_OUT_BOUND_SUFFIXES.has(out[out.length - 1])
        && toNorm(out[out.length - 2]).length >= 2) {
      const suffix = out.pop();
      out[out.length - 1] += suffix;
    }
  });
  return out;
}

// Shipped word-pair table (engine/space-out-bigrams-data.js), injected by the worker.
// Null until then, so importing the segmenter alone keeps ranking on the pure-unigram
// path — nothing runs at import. Values are precomputed log(1 + count), summed and
// weighted in bigramBonus, so the parse never happens twice and the hot path only adds.
let spaceOutBigrams = null;
export function configureSpaceOutBigrams(raw) {
  if (!raw) { spaceOutBigrams = null; return; }
  const m = new Map();
  for (const line of raw.trim().split('\n')) {
    if (!line) continue;
    const at = line.lastIndexOf(' ');
    m.set(line.slice(0, at), Math.log(1 + Number(line.slice(at + 1))));
  }
  spaceOutBigrams = m.size ? m : null;
}

function bigramBonus(norms) {
  if (!spaceOutBigrams) return 0;
  let bonus = 0;
  for (let i = 0; i < norms.length - 1; i++) {
    const b = spaceOutBigrams.get(`${norms[i]} ${norms[i + 1]}`);
    if (b !== undefined) bonus += b;
  }
  return SPACE_OUT_BIGRAM_WEIGHT * bonus;
}

// Injected so this engine module never imports the data layer (IDB/localStorage).
let _idbGet = null;
let _idbPut = null;

export function configureIO({ idbGet, idbPut }) {
  _idbGet = idbGet;
  _idbPut = idbPut;
}

let unigramLogFreqs = null;
let unigramMinLogFreq = -Infinity;
let unigramLoadPromise = null;

export function setUnigramCorpus(freqs, minLog) {
  const entries = Object.entries(freqs);
  unigramLogFreqs = new Map(entries);
  unigramMinLogFreq = minLog !== undefined ? minLog
    : entries.length ? Math.min(...entries.map(([, lf]) => lf)) : -Infinity;
  unigramLoadPromise = null;
}

export function invalidateUnigramCorpus() {
  unigramLogFreqs = null;
  unigramLoadPromise = null;
}

function creditedStem(word) {
  if (!unigramLogFreqs) return null;
  let best = null;
  let bestLf = -Infinity;
  const tryStem = s => {
    const lf = unigramLogFreqs.get(s);
    if (lf !== undefined && lf > bestLf) { best = s; bestLf = lf; }
  };
  for (const suf of SPACE_OUT_STEM_SUFFIXES) {
    if (!word.endsWith(suf)) continue;
    const stemLen = word.length - suf.length;
    if (stemLen < 2) continue;
    const stem = word.slice(0, stemLen);
    tryStem(stem);
    if (suf === 'ed' || suf === 'ing' || suf === 'er' || suf === 'est') {
      tryStem(stem + 'e');  // raced, racing, racer, ...
    }
    if (Y_TO_I_SUFFIXES.has(suf)) tryStem(stem + 'y');
  }
  return best;
}

export function morphemeStemLogFreq(word) {
  const stem = creditedStem(word);
  return stem === null ? -Infinity : unigramLogFreqs.get(stem);
}

export function unigramLogFreq(word) {
  const lf = unigramLogFreqs?.get(word);
  if (lf !== undefined) return lf;
  const stemLf = morphemeStemLogFreq(word);
  if (stemLf > -Infinity) return stemLf - SPACE_OUT_MORPHEME_PENALTY;
  return unigramMinLogFreq - word.length * SPACE_OUT_OOV_PER_LETTER;
}

// wordfreq carries hashtag run-togethers (HIGHROAD, REDLIGHT, HOTWATER) as tokens, and
// each one glues the phrase it came from: TOOK THE HIGHROAD. A rare token that splits
// into two pieces, each far commoner than itself, is discounted when ranking splits.
// That spaces out some closed compounds (MOUNTAIN TOP), which read fine either way.
// Pieces run 3+ letters, since IN, BE, and BY would split INGROWN and BEHELD too.
export const SPACE_OUT_GLUED_MAX_LOG_FREQ = -12;
export const SPACE_OUT_GLUED_PIECE_MARGIN = 4;
export const SPACE_OUT_GLUED_PENALTY = 6;
const GLUED_MIN_PIECE = 3;

function isGluedToken(token) {
  const lf = unigramLogFreqs.get(token);
  if (lf === undefined || lf >= SPACE_OUT_GLUED_MAX_LOG_FREQ) return false;
  const floor = lf + SPACE_OUT_GLUED_PIECE_MARGIN;
  for (let i = GLUED_MIN_PIECE; i <= token.length - GLUED_MIN_PIECE; i++) {
    if (unigramLogFreqs.get(token.slice(0, i)) >= floor
        && unigramLogFreqs.get(token.slice(i)) >= floor) return true;
  }
  return false;
}

// A stem-credited word carries its stem's discount. Otherwise the credit is a way around
// it, and the next word's S moves over to take it: DUTYFREES HOP, SECONDHANDS TORE.
function rankingLogFreq(part) {
  const lf = unigramLogFreq(part);
  if (!unigramLogFreqs) return lf;
  const token = unigramLogFreqs.has(part) ? part : creditedStem(part);
  return token !== null && isGluedToken(token) ? lf - SPACE_OUT_GLUED_PENALTY : lf;
}

export function msgpackDecode(bytes) {
  const td = new TextDecoder('utf-8');
  let pos = 0;
  const u8 = () => bytes[pos++];
  const u16 = () => { const v = (bytes[pos] << 8) | bytes[pos + 1]; pos += 2; return v; };
  const u32 = () => {
    const v = bytes[pos] * 0x1000000 + ((bytes[pos + 1] << 16) | (bytes[pos + 2] << 8) | bytes[pos + 3]);
    pos += 4;
    return v;
  };
  const str = (len) => { const s = td.decode(bytes.subarray(pos, pos + len)); pos += len; return s; };
  const arr = (n) => { const out = new Array(n); for (let i = 0; i < n; i++) out[i] = readVal(); return out; };
  const map = (n) => { const out = {}; for (let i = 0; i < n; i++) { const k = readVal(); out[k] = readVal(); } return out; };
  function readVal() {
    const t = u8();
    if (t <= 0x7f) return t;
    if (t <= 0x8f) return map(t & 0x0f);
    if (t <= 0x9f) return arr(t & 0x0f);
    if (t <= 0xbf) return str(t & 0x1f);
    if (t === 0xd9) return str(u8());
    if (t === 0xda) return str(u16());
    if (t === 0xdb) return str(u32());
    if (t === 0xdc) return arr(u16());
    if (t === 0xdd) return arr(u32());
    if (t === 0xde) return map(u16());
    if (t === 0xdf) return map(u32());
    throw new Error(`msgpack: unsupported type 0x${t.toString(16)}`);
  }
  return readVal();
}

async function gunzipBytes(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export function buildCorpusFromMsgpack(decoded) {
  const map = new Map();
  let lastNonEmpty = 1;
  for (let bucket = 1; bucket < decoded.length; bucket++) {
    const words = decoded[bucket];
    if (!words || words.length === 0) continue;
    const logFreq = -bucket * Math.LN10 / 100;
    for (const word of words) map.set(word, logFreq);
    lastNonEmpty = bucket;
  }
  return { map, minLog: -lastNonEmpty * Math.LN10 / 100 };
}

export async function loadUnigramCorpus() {
  if (unigramLogFreqs) return;
  if (unigramLoadPromise) return unigramLoadPromise;
  unigramLoadPromise = (async () => {
    const cached = await _idbGet(UNIGRAM_CORPUS_IDB_KEY);
    if (cached && cached.map) {
      unigramLogFreqs = cached.map;
      unigramMinLogFreq = cached.minLog;
      return;
    }
    const resp = await fetch(UNIGRAM_CORPUS_URL);
    if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
    const gz = await resp.arrayBuffer();
    const decompressed = await gunzipBytes(new Uint8Array(gz));
    const { map, minLog } = buildCorpusFromMsgpack(msgpackDecode(decompressed));
    unigramLogFreqs = map;
    unigramMinLogFreq = minLog;
    await _idbPut(UNIGRAM_CORPUS_IDB_KEY, { map, minLog });
    const size = resp.headers.get('content-length');
    if (size) await _idbPut(UNIGRAM_CORPUS_SIZE_KEY, size);
  })();
  try {
    await unigramLoadPromise;
  } finally {
    if (!unigramLogFreqs) unigramLoadPromise = null;
  }
}

export function hasUnigramCorpus() {
  return !!unigramLogFreqs;
}

const isDigit = c => c >= '0' && c <= '9';
const splitsMidDigit = (s, i) => i < s.length && isDigit(s[i - 1]) && isDigit(s[i]);
const isAllowedPart = (p, wordlist) => p.length <= 2 || wordlist.norms.has(p);

// Well-formed, not merely Roman letters: DID I and MMM MMM MMM MMM are all-Roman and
// must still split, and neither is a valid numeral. A numeral is never words.
const ROMAN_NUMERAL = /^m{0,4}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/;
export const isRomanNumeral = entry => entry.length > 0 && ROMAN_NUMERAL.test(entry);

export function rankedSplits(entry, window, wordlist) {
  if (entry.length < 1) return [];
  if (isRomanNumeral(entry)) return [[entry]];

  const bestMemo = new Map();
  bestMemo.set('', 0);
  function bestFor(s) {
    const hit = bestMemo.get(s);
    if (hit !== undefined) return hit;
    let best = -Infinity;
    for (let i = 1; i <= s.length; i++) {
      if (splitsMidDigit(s, i)) continue;
      const p = s.slice(0, i);
      if (!isAllowedPart(p, wordlist)) continue;
      const score = rankingLogFreq(p) - SPACE_OUT_PART_PENALTY + bestFor(s.slice(i));
      if (score > best) best = score;
    }
    bestMemo.set(s, best);
    return best;
  }
  const overallBest = bestFor(entry);
  const threshold = overallBest - window;

  const results = [];
  const acc = [];
  function enumerate(s, accScore) {
    if (s === '') {
      if (accScore >= threshold) {
        results.push({ score: accScore, parts: acc.slice() });
      }
      return;
    }
    if (accScore + bestFor(s) < threshold) return;
    for (let i = 1; i <= s.length; i++) {
      if (splitsMidDigit(s, i)) continue;
      const p = s.slice(0, i);
      if (!isAllowedPart(p, wordlist)) continue;
      acc.push(p);
      enumerate(s.slice(i), accScore + rankingLogFreq(p) - SPACE_OUT_PART_PENALTY);
      acc.pop();
    }
  }
  enumerate(entry, 0);

  // Expand overridden parts, then rank by the enumeration score plus the bigram bonus.
  // Expanding before the sort lets the bonus see the corrected parts (`of the`, not the
  // glued `ofthe`) and lets two splits that coincide once expanded de-dup; the override
  // itself still fires wherever a glued part lands, mid-entry as readily as whole-entry.
  // With no bigram table the bonus is 0, so the order is the pure-unigram score untouched.
  const richness = ps => ps.join(' ').replace(/[a-z0-9 ]/g, '').length;
  const scored = results.map(({ score, parts }) => {
    const expanded = joinAffixes(parts.flatMap(p => SPACE_OUT_OVERRIDES.get(p)?.split(' ') ?? [p]));
    const norms = expanded.map(toNorm);
    return { parts: expanded, norms, score: score + bigramBonus(norms) };
  });
  scored.sort((a, b) => b.score - a.score);
  // Dedup on the norm-folded key so an override's `I don't` merges with the scorer's own
  // `i dont`; spaces stay significant, so distinct spacings stay distinct. On a collision
  // keep the richer rendering — the override's, since the scorer only emits bare norms.
  const seen = new Map();
  const out = [];
  for (const { parts, norms } of scored) {
    const key = norms.join(' ');
    const at = seen.get(key);
    if (at === undefined) { seen.set(key, out.length); out.push(parts); }
    else if (richness(parts) > richness(out[at])) out[at] = parts;
  }
  return out;
}

// ─── Compound readings ───────────────────────────────────────────────────────
//
// rankedSplits ranks the unsplit entry first whenever the corpus carries the glued
// form as a token of its own and it outscores every split: RICKROLL beats RICK ROLL
// by 4.04 even after the glued-token discount.
// bestCompoundSplit answers a caller that needs a reading regardless: the best split
// into 2+ parts, with the unsplit form off the table.
//
// The thresholds are what keep the guess safe — the floor separates RICK ROLL from
// APPEL LEE, the tail length BACK SWING from FUN DIE. Loosen either and thousands of
// entries rhyme on invented syllables, with nothing on screen to say so.
export const SPACE_OUT_COMPOUND_FLOOR = -11;
export const SPACE_OUT_COMPOUND_MIN_TAIL = 3;

const SPACE_OUT_SUFFIX_SET = new Set(SPACE_OUT_SUFFIXES);

function bestMultiPartSplit(entry, wordlist) {
  const memo = new Map([['', { score: 0, parts: [] }]]);
  function bestFrom(s) {
    const hit = memo.get(s);
    if (hit) return hit;
    let best = { score: -Infinity, parts: null };
    for (let i = 1; i <= s.length; i++) {
      if (splitsMidDigit(s, i)) continue;
      const p = s.slice(0, i);
      if (!isAllowedPart(p, wordlist)) continue;
      const rest = bestFrom(s.slice(i));
      if (!rest.parts) continue;
      const score = unigramLogFreq(p) - SPACE_OUT_PART_PENALTY + rest.score;
      if (score > best.score) best = { score, parts: [p, ...rest.parts] };
    }
    memo.set(s, best);
    return best;
  }
  let best = { score: -Infinity, parts: null };
  for (let i = 1; i < entry.length; i++) {
    if (splitsMidDigit(entry, i)) continue;
    const p = entry.slice(0, i);
    if (!isAllowedPart(p, wordlist)) continue;
    const rest = bestFrom(entry.slice(i));
    if (!rest.parts) continue;
    const score = unigramLogFreq(p) - SPACE_OUT_PART_PENALTY + rest.score;
    if (score > best.score) best = { score, parts: [p, ...rest.parts] };
  }
  return best.parts;
}

const isConfidentCompound = (parts, wordlist) =>
  !!parts
  && parts[parts.length - 1].length >= SPACE_OUT_COMPOUND_MIN_TAIL
  && parts.every(p => p.length > 1 && !SPACE_OUT_SUFFIX_SET.has(p) && wordlist.norms.has(p)
    && unigramLogFreq(p) >= SPACE_OUT_COMPOUND_FLOOR);

function inflectionOf(word) {
  for (const suf of SPACE_OUT_SUFFIXES) {
    if (!word.endsWith(suf)) continue;
    const stemLen = word.length - suf.length;
    if (stemLen < 2) continue;
    const bare = word.slice(0, stemLen);
    const restored = suf === 'ed' || suf === 'ing' || suf === 'er' || suf === 'est' ? bare + 'e'
      : suf === 'ies' || suf === 'ied' ? bare + 'y'
      : null;
    for (const stem of restored ? [bare, restored] : [bare]) {
      if (unigramLogFreqs.has(stem)) return { stem, suffix: word.slice(stemLen) };
    }
  }
  return null;
}

export function bestCompoundSplit(entry, wordlist) {
  if (!unigramLogFreqs || isRomanNumeral(entry)) return null;
  const inflected = inflectionOf(entry);
  // Split the stem, not the inflected form, so the ending rides along instead of
  // steering: RICKROLLING is RICK ROLLING, where splitting directly reads CALL USING.
  const parts = bestMultiPartSplit(inflected ? inflected.stem : entry, wordlist);
  if (!isConfidentCompound(parts, wordlist)) return null;
  if (!inflected) return parts;
  const last = parts[parts.length - 1];
  // The stem search may have restored an e/y the entry's own spelling drops again.
  const base = entry.endsWith(last + inflected.suffix) ? last : last.slice(0, -1);
  return parts.slice(0, -1).concat(base + inflected.suffix);
}
